import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { Wrench as A1, DollarSign as A2, Clock as A3, HardHat as A4, GraduationCap as A5, Building2 as A6, ShieldCheck as A7 } from 'lucide-react';

import { AppLayout, GlassCard, PageHeader } from '../components/Layout';
import PathFitDashboard from '../components/paths/PathFitDashboard';
import { Button } from '../components/ui/button';
import { 
  Wrench, Zap, Droplet, Thermometer, HardHat, Truck,
  Plus, ExternalLink, DollarSign, TrendingUp, Clock,
  Award, BookOpen, CheckCircle, MapPin, Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Skilled Trades, 2024 BLS median wages, 2023-2033 employment projections.
// Wage ranges show the 25th-75th percentile band nationally; growth figures come
// from the current BLS Occupational Outlook release.
const TRADES = [
  {
    id: 'electrician',
    title: 'Electrician',
    icon: Zap,
    color: 'amber',
    salary: '$62,000, $103,000',
    growth: '+11% through 2033',
    description: 'Install, maintain, and repair electrical systems in homes, businesses, and industrial facilities. Median 2024 wage: $62,350.',
    certifications: ['Journeyman License', 'Master Electrician', 'OSHA 10/30'],
    apprenticeship: '4-5 years paid',
    aiImpact: 'AI-Resistant · Requires on-site troubleshooting',
    resources: [
      { name: 'IBEW (Electricians Union)', url: 'https://ibew.org' },
      { name: 'NECA Apprenticeship', url: 'https://www.necanet.org/workforce-development' },
    ],
  },
  {
    id: 'plumber',
    title: 'Plumber',
    icon: Droplet,
    color: 'blue',
    salary: '$56,000, $96,000',
    growth: '+6% through 2033',
    description: 'Install and repair water, drainage, and gas piping systems in residential and commercial buildings. Median 2024 wage: $61,550.',
    certifications: ['Journeyman License', 'Master Plumber', 'Backflow Prevention'],
    apprenticeship: '4-5 years paid',
    aiImpact: 'AI-Resistant · Complex problem-solving in unique jobsites',
    resources: [
      { name: 'UA (Plumbers Union)', url: 'https://www.ua.org' },
      { name: 'PHCC Training', url: 'https://www.phccweb.org' },
    ],
  },
  {
    id: 'hvac',
    title: 'HVAC Technician',
    icon: Thermometer,
    color: 'cyan',
    salary: '$51,000, $86,000',
    growth: '+9% through 2033',
    description: 'Install and maintain heating, ventilation, air conditioning, and refrigeration systems. Median 2024 wage: $57,300.',
    certifications: ['EPA 608', 'NATE Certification', 'R-410A Handling'],
    apprenticeship: '3-5 years',
    aiImpact: 'AI-Enhanced · Smart systems raise demand for skilled techs',
    resources: [
      { name: 'HVAC Excellence', url: 'https://www.hvacexcellence.org' },
      { name: 'RSES Training', url: 'https://www.rses.org' },
    ],
  },
  {
    id: 'welder',
    title: 'Welder',
    icon: HardHat,
    color: 'orange',
    salary: '$45,000, $92,000',
    growth: '+2% through 2033',
    description: 'Join metal parts using various welding techniques for construction, manufacturing, and repair. Median 2024 wage: $50,910.',
    certifications: ['AWS Certification', 'CWI Inspector', 'Pipe Welding Cert'],
    apprenticeship: '3-4 years',
    aiImpact: 'AI-Enhanced · Robotics raises demand for skilled welders',
    resources: [
      { name: 'AWS (Welding Society)', url: 'https://www.aws.org' },
      { name: 'Lincoln Electric Training', url: 'https://www.lincolnelectric.com/en-us/education-center' },
    ],
  },
  {
    id: 'carpenter',
    title: 'Carpenter',
    icon: Wrench,
    color: 'brown',
    salary: '$47,000, $80,000',
    growth: '+4% through 2033',
    description: 'Build, install, and repair structures and fixtures made of wood and other materials. Median 2024 wage: $58,210.',
    certifications: ['Journeyman Carpenter', 'OSHA 30', 'Lead Renovation'],
    apprenticeship: '3-4 years',
    aiImpact: 'AI-Resistant · Creative problem-solving and craftsmanship',
    resources: [
      { name: 'UBC (Carpenters Union)', url: 'https://www.carpenters.org' },
      { name: 'HBI Training', url: 'https://hbi.org' },
    ],
  },
  {
    id: 'solar_tech',
    title: 'Solar Panel Installer',
    icon: Zap,
    color: 'yellow',
    salary: '$45,000, $78,000',
    growth: '+48% through 2033',
    description: 'Install and maintain solar panel systems on rooftops and ground-mounted arrays. Median 2024 wage: $51,860.',
    certifications: ['NABCEP Certification', 'OSHA Safety', 'Electrical Basics'],
    apprenticeship: '1-2 years',
    aiImpact: 'HIGH DEMAND · Clean-energy transition driving explosive growth',
    resources: [
      { name: 'NABCEP', url: 'https://www.nabcep.org' },
      { name: 'Solar Energy International', url: 'https://www.solarenergy.org' },
    ],
  },
  {
    id: 'ev_tech',
    title: 'EV Technician',
    icon: Truck,
    color: 'green',
    salary: '$55,000, $88,000',
    growth: '+9% through 2033',
    description: 'Diagnose, repair, and maintain electric vehicles and charging infrastructure. Median 2024 wage: $49,690 for automotive service techs; EV specialists earn a premium.',
    certifications: ['ASE Certification', 'High Voltage Safety', 'EV Manufacturer Training'],
    apprenticeship: '2-3 years',
    aiImpact: 'HIGH DEMAND · EV adoption creating new career paths',
    resources: [
      { name: 'ASE Certification', url: 'https://www.ase.com' },
      { name: 'Tesla Training', url: 'https://www.tesla.com/careers' },
    ],
  },
  {
    id: 'wind_tech',
    title: 'Wind Turbine Technician',
    icon: TrendingUp,
    color: 'teal',
    salary: '$56,000, $80,000',
    growth: '+60% through 2033 (fastest-growing job in America)',
    description: 'Install, inspect, maintain, and repair wind turbines for power generation. Median 2024 wage: $61,770.',
    certifications: ['GWO Certification', 'OSHA Safety', 'Tower Climbing'],
    apprenticeship: '1-2 years',
    aiImpact: 'HIGHEST GROWTH · BLS-projected #1 fastest growing occupation',
    resources: [
      { name: 'AWEA WindPower', url: 'https://cleanpower.org' },
      { name: 'BLS OOH, Wind Turbine Techs', url: 'https://www.bls.gov/ooh/installation-maintenance-and-repair/wind-turbine-technicians.htm' },
    ],
  },
];

export default function SkilledTradesPage() {
  const [arrived, setArrived] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [activeTab, setActiveTab] = useState('trades');
  const [filterType, setFilterType] = useState('all');

  const addToGoals = async (item, category = 'career') => {
    try {
      await axios.post(`${API}/goals`, {
        title: item.title || item,
        description: item.description || `Career goal from Skilled Trades`,
        category: category,
        priority: 'medium',
        status: 'backlog',
        source: 'Skilled Trades'
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Added to your Goals! 🎯');
    } catch (error) {
      toast.error('Failed to add to goals');
    }
  };

  const filteredTrades = filterType === 'all' 
    ? TRADES 
    : filterType === 'highgrowth' 
      ? TRADES.filter(t => {
          const m = t.growth.match(/\+(\d+)/);
          return m && parseInt(m[1], 10) > 10;
        })
      : TRADES;

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="trades-arrival"
        eyebrow="Skilled Trades"
        title="Get paid while you learn the trade."
        subtitle="Electricians, plumbers, welders, HVAC, linemen, machinists. Four to five years of paid apprenticeship instead of four years of tuition, and at the end, a licence nobody can outsource and work that can't be done from a laptop."
        accent="#fbbf24"
        second="#f472b6"
        ctaLabel="Explore the trades"
        narration={"This is Skilled Trades. These are not fallback options, they're licensed professions with real ceilings. In most trades you earn from day one as an apprentice, your training is paid for, and you finish with a credential the law requires somebody to hold. We'll show you what each trade pays at apprentice, journeyman and master level, how long the licence takes, what it costs you, and where the work actually is. If you'd rather be paid to learn than pay to learn, start here."}
        facts={[
          { icon: A2, head: 'Earn from day one', sub: 'Apprentices are paid employees, not students' },
          { icon: A3, head: '4-5 years to licensed', sub: 'Journeyman, then master, with pay rising at each step' },
          { icon: A7, head: 'Hard to automate', sub: 'Hands-on work in physical places' },
        ]}
        modulesTitle="What you'll find here"
        modules={[
          { icon: A1, label: 'Trade by trade', line: 'Pay at apprentice, journeyman and master, plus what the day actually looks like.', hue: '#fbbf24' },
          { icon: A4, label: 'How you get in', line: 'Union halls, non-union programmes, pre-apprenticeships and what they ask for.', hue: '#f472b6' },
          { icon: A5, label: 'Licensing', line: 'Hours required, exams, and how licences move between states.', hue: '#22d3ee' },
          { icon: A6, label: 'Where the work is', line: 'The states and industries hiring, and what they pay there.', hue: '#34d399' },
          { icon: A2, label: 'Cost versus a degree', line: 'What you spend, what you earn during, and where you land four years on.', hue: '#a78bfa' },
          { icon: A3, label: 'Your next step', line: 'One concrete action, the programme to call, the form to file.', hue: '#60a5fa' },
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader 
        title="Skilled Trades"
        description="Licensed professions that pay you to train, and can't be outsourced."
      />

      {/* Workforce Pell funding banner, brand cyan/violet, no gradient bleed */}
      <div className="mb-6 rounded-2xl border border-cyan-400/25 bg-cyan-500/[0.06] p-4"
        style={{ boxShadow: '0 20px 60px -50px rgba(34,211,238,0.5)' }}
        data-testid="trades-workforce-pell-banner">
        <div className="flex items-start gap-3">
          <HardHat className="w-5 h-5 text-cyan-300 flex-shrink-0 mt-0.5" />
          <p className="text-slate-200 text-sm leading-relaxed">
            <strong className="text-white">New in 2026:</strong> Under the One Big Beautiful Bill Act,
            <strong className="text-cyan-200"> Workforce Pell Grants</strong> now help pay for short-term (8-15 week) job-training programs in many
            skilled trades, making these high-demand, low-debt careers more affordable than ever.
          </p>
        </div>
      </div>

      {/* Fit dashboard, same "is this a good match?" strip as the College page */}
      <PathFitDashboard
        pathLabel="the skilled trades"
        fit={{ overall: 78, career: 74, financial: 92, timeline: 88 }}
        reasons={['Hands-on work matches your strengths', 'Zero debt to enter', 'Earning within 1-2 years', 'High-growth clean-energy roles']}
      />

      {/* Quick Stats, brand cyan/violet */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <GlassCard className="text-center">
          <DollarSign className="w-6 h-6 mx-auto mb-2 text-cyan-300" />
          <p className="text-2xl font-bold text-white">$62k</p>
          <p className="text-xs text-slate-300">Median 2024 wage</p>
        </GlassCard>
        <GlassCard className="text-center">
          <TrendingUp className="w-6 h-6 mx-auto mb-2 text-violet-300" />
          <p className="text-2xl font-bold text-white">+60%</p>
          <p className="text-xs text-slate-300">Wind techs · fastest-growing job</p>
        </GlassCard>
        <GlassCard className="text-center">
          <Clock className="w-6 h-6 mx-auto mb-2 text-sky-300" />
          <p className="text-2xl font-bold text-white">2-5 yrs</p>
          <p className="text-xs text-slate-300">Paid apprenticeship</p>
        </GlassCard>
        <GlassCard className="text-center">
          <Award className="w-6 h-6 mx-auto mb-2 text-pink-300" />
          <p className="text-2xl font-bold text-white">$0</p>
          <p className="text-xs text-slate-300">Debt to enter</p>
        </GlassCard>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={() => setFilterType('all')}
          className={`px-4 py-2 rounded-full text-sm border transition-colors ${filterType === 'all' ? 'border-cyan-300/60 bg-cyan-300/15 text-white' : 'border-white/12 text-slate-300 hover:text-white'}`}
        >
          All Trades
        </button>
        <button
          onClick={() => setFilterType('highgrowth')}
          className={`px-4 py-2 rounded-full text-sm border flex items-center gap-2 transition-colors ${filterType === 'highgrowth' ? 'border-violet-300/60 bg-violet-400/15 text-white' : 'border-white/12 text-slate-300 hover:text-white'}`}
        >
          <TrendingUp className="w-3.5 h-3.5" />
          High Growth (AI-Era)
        </button>
      </div>

      {/* Trades Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTrades.map(trade => {
          const Icon = trade.icon;
          const growthMatch = trade.growth.match(/\+(\d+)/);
          const isHighGrowth = growthMatch && parseInt(growthMatch[1], 10) > 10;
          
          return (
            <GlassCard 
              key={trade.id}
              className={`cursor-pointer transition-all hover:scale-[1.02] ${
                selectedTrade?.id === trade.id ? 'ring-2 ring-blue-500' : ''
              } ${isHighGrowth ? 'border-green-500/30' : ''}`}
              onClick={() => setSelectedTrade(selectedTrade?.id === trade.id ? null : trade)}
            >
              {isHighGrowth && (
                <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                  HOT 🔥
                </div>
              )}
              
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 bg-${trade.color}-500/20 rounded-xl flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 text-${trade.color}-400`} />
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-white mb-1">{trade.title}</h3>
              <p className="text-slate-300 text-sm mb-4">{trade.description}</p>
              
              <div className="flex items-center gap-4 text-sm mb-3">
                <div className="flex items-center gap-1 text-emerald-400">
                  <DollarSign className="w-4 h-4" />
                  {trade.salary}
                </div>
                <div className={`flex items-center gap-1 ${isHighGrowth ? 'text-green-400' : 'text-blue-400'}`}>
                  <TrendingUp className="w-4 h-4" />
                  {trade.growth}
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-xs text-slate-300">
                <Clock className="w-3 h-3" />
                {trade.apprenticeship} apprenticeship
              </div>
              
              {selectedTrade?.id === trade.id && (
                <div className="mt-4 pt-4 border-t border-navy-700 space-y-4">
                  <div className="p-3 bg-navy-900/50 rounded-lg">
                    <p className="text-sm text-slate-300">{trade.aiImpact}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-slate-300 mb-2">Certifications:</h4>
                    <div className="flex flex-wrap gap-2">
                      {trade.certifications.map(cert => (
                        <span key={cert} className="px-2 py-1 bg-navy-700 text-slate-300 text-xs rounded-full">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-slate-300 mb-2">Resources:</h4>
                    {trade.resources.map(resource => (
                      <a
                        key={resource.name}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center justify-between p-2 bg-navy-900/50 rounded-lg hover:bg-navy-800 mb-2"
                      >
                        <span className="text-sm text-white">{resource.name}</span>
                        <ExternalLink className="w-4 h-4 text-blue-400" />
                      </a>
                    ))}
                  </div>
                  
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      addToGoals(trade);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add to My Goals
                  </Button>
                </div>
              )}
            </GlassCard>
          );
        })}
      </div>

      {/* Why Trades Section */}
      <GlassCard className="mt-8">
        <h3 className="text-xl font-bold text-white mb-4">Why Consider Skilled Trades?</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: DollarSign, title: 'No Student Debt', desc: 'Earn while you learn through paid apprenticeships' },
            { icon: TrendingUp, title: 'High Demand', desc: '3+ million skilled workers needed in the next decade' },
            { icon: MapPin, title: 'Work Anywhere', desc: 'Skills transfer across states and even countries' },
            { icon: Users, title: 'Job Security', desc: "Can't be outsourced or fully automated" },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="p-4 bg-navy-900/50 rounded-lg">
                <Icon className="w-8 h-8 text-blue-400 mb-2" />
                <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                <p className="text-sm text-slate-300">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </GlassCard>
    </AppLayout>
  );
}
